/**
 * Moderation detail model (doc 09 §4.4).
 *
 * The wire DTO (`AdminReportDetailDTO`) is ONE flat, denormalized object where only the field group
 * matching `contentType` is populated. We narrow it into a discriminated union keyed by `kind` so the
 * variant cards get typed access to their group with no casts. All fields we surface are made concrete
 * (`| null` instead of `?`) so `exactOptionalPropertyTypes` never forces us to assign a bare `undefined`.
 */

export type ModerationKind = 'USER' | 'CONTENT' | 'COMMENT' | 'MESSAGE' | 'FEEDBACK';

/** The adapter only ever yields these two — CMP never produces UNDER_REVIEW / RESOLVED (doc 09 §2.2). */
export type ModerationStatus = 'PENDING' | 'DISMISSED';

/** Priority is cosmetic — the backend does not send one, CMP hardcodes HIGH (doc 09 §2.2, risk #6). */
export type ModerationPriority = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

export interface PersonInfo {
  name: string;
  userId: string;
  uname: string;
  email: string;
  phone: string;
  avatar: string | null;
}

export interface HistoryEntry {
  action: string;
  date: string;
  moderator: string;
  moderatorRole: string | null;
  description: string;
}

export type MediaKind = 'VID' | 'IMG' | 'AUD';

export interface MediaFile {
  id: string;
  fileUrl: string | null;
  thumbnail: string | null;
  /** Mux playback id for HLS video, when present. */
  playbackId: string | null;
  kind: MediaKind;
}

export interface ContentTotals {
  like: number;
  comment: number;
  view: number;
  share: number;
  bookmark: number;
}

export interface ReportedContent {
  /** Post id (the report's contentId). Drives take-down / list. */
  id: string;
  title: string;
  creator: string;
  creatorUserId: string;
  type: string;
  status: string;
  uploadDate: string;
  price: string;
  country: string;
  ip: string;
  device: string;
  collaborator: string | null;
  hashtags: string | null;
  thumbnail: string | null;
  mediaFiles: MediaFile[];
  totals: ContentTotals;
  totalEarned: string | null;
  totalTips: string | null;
  releaseTime: string | null;
  expirationTime: string | null;
}

export interface CommentNode {
  id: string;
  text: string;
  author: string;
  authorUserId: string;
  authorUname: string;
  date: string;
  likes: number;
  avatar: string | null;
  isReported: boolean;
  children: CommentNode[];
  replyToUname: string | null;
}

export interface ReportedComment {
  /** Root of the comment thread (may be null when the tree is absent). */
  tree: CommentNode | null;
  /** The specific comment id to delete (the reported node) — DELETE /comment/{id}. */
  reportedCommentId: string | null;
  postTitle: string;
  postId: string;
  platform: string;
}

export interface MessageNode {
  id: string;
  text: string;
  author: string;
  authorUserId: string;
  authorUname: string;
  avatar: string | null;
  createdAt: string | null;
  /** TEXT | TIP | POST | EXCLUSIVE | SHARE | COLLABORATOR | STORY. */
  contentType: string | null;
  postId: number | null;
  isReported: boolean;
}

export interface ReportedMessage {
  messages: MessageNode[];
  platform: string;
}

/** USER-only extra profile fields (doc 09 §3 user group). */
export interface ModerationUserExtra {
  pricePerMonth: string | null;
  registeredAt: string | null;
  lastLogin: string | null;
}

interface Base {
  id: string;
  reportId: string; // "REP-<id>"
  contentId: string | null;
  reason: string;
  description: string;
  useCase: string | null;
  status: ModerationStatus;
  priority: ModerationPriority;
  reportedDate: string;
  reporter: PersonInfo;
  /** Resolution differs by kind (doc 09 §2.2). Null for FEEDBACK. */
  reportedUser: PersonInfo | null;
  /**
   * The reported user's CURRENT account status code (ACT/INA/TER), resolved server-side for whichever
   * person `reportedUser` points to. Drives the single Ban/Unban toggle (TER -> "Unban", else "Ban").
   * Null when the DTO didn't resolve a reported user (e.g. FEEDBACK, or the account was hard-deleted).
   */
  reportedUserStatus: string | null;
  history: HistoryEntry[];
}

export type ModerationDetail =
  | (Base & { kind: 'USER'; user: ModerationUserExtra })
  | (Base & { kind: 'FEEDBACK'; attachments: string[] })
  | (Base & { kind: 'CONTENT'; content: ReportedContent })
  | (Base & { kind: 'COMMENT'; comment: ReportedComment })
  | (Base & { kind: 'MESSAGE'; message: ReportedMessage });

/** contentType code -> kind (doc 09 §2.2). Unknown codes fall back to CONTENT. */
export const CONTENT_TYPE_TO_KIND: Record<string, ModerationKind> = {
  P: 'CONTENT',
  S: 'CONTENT',
  H: 'CONTENT',
  F: 'CONTENT',
  L: 'CONTENT',
  C: 'COMMENT',
  U: 'USER',
  B: 'FEEDBACK',
  M: 'MESSAGE',
};

export function contentTypeToKind(
  code: string | null | undefined,
): ModerationKind {
  const key = (code ?? '').toString().trim().toUpperCase();
  return CONTENT_TYPE_TO_KIND[key] ?? 'CONTENT';
}



// --- Coop Intelligence types (Phase 1) ---

export interface SignalScore {
  signal_id: string;
  display_name: string;
  channel: string;
  term: string;
  score: number;
  threshold: number;
}

export interface RuleEvaluation {
  rule_id: string;
  rule_name: string;
  description: string;
  condition_summary: string;
  matched: boolean;
  match_reason: string | null;
  action_name: string;
  action_type: string;
  linked_policies: string[];
  signal_values: Record<string, number>;
}

export interface EnforcementAction {
  action: string;
  verdict: string;
  actor_id: string;
  reason: string;
  executed_at: string | null;
}

export interface PipelineStep {
  stage: string;
  title: string;
  status: 'completed' | 'running' | 'pending' | 'failed';
  duration_ms: number | null;
  description: string;
}

export interface ModerationIntelligence {
  case_id: string;
  /** Coop / media-manager lifecycle (processing, heuristic, standard, deep, completed, …). */
  case_status: string;
  verdict: string | null;
  verdict_reason: string | null;
  vectors: Record<string, Record<string, number>> | null;
  // Optional on purpose: the Coop context omits empty collections, and `pipeline_steps` is not on
  // the admin DTO at all. Declaring them optional makes an unguarded read a compile error rather
  // than a render-time "Cannot read properties of undefined".
  signals?: SignalScore[];
  rule_evaluations?: RuleEvaluation[];
  enforcement_actions?: EnforcementAction[];
  /**
   * Planned Coop enforcement from the live case. Media-manager often returns these
   * while leaving `enforcement_actions` empty after AUTO_REJECT / AUTO_APPROVE.
   */
  enforcement_previews?: EnforcementAction[];
  pipeline_steps?: PipelineStep[];
  coop_url: string;
  /** TipMi-recorded wall time for successful Coop moderation-deep (ms). */
  deep_scan_duration_ms?: number | null;
  /** GPU vision / LLM model id (e.g. google/gemma-4-E4B-it). */
  gpu_model?: string | null;
  /** Bedrock Frontier model id when frontier review ran (e.g. openai.gpt-5.6-luna). */
  frontier_model?: string | null;
  /**
   * TipMi outbox → media-manager delivery phase.
   * Present when mirror exists or when admin returns an awaiting-delivery stub.
   */
  delivery_status?: string | null;
  delivery_attempt_count?: number | null;
  delivery_last_error?: string | null;
  delivery_last_stage?: string | null;
  delivery_next_attempt_at?: string | null;
  /** TipMi outbox last attempt (modified_at / delivered_at). */
  delivery_last_attempt_at?: string | null;
}
